import React, { useContext, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { DateField, FlexColumn, FlexMiddle, Header2, InputField, SelectField } from '@ubeya/shared-web/components';
import { AccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useField, useForm } from 'react-final-form';
import { ACCOUNTS_CONTRACTS_TYPES, ContractTypesWithMinimums } from '@ubeya/shared/constants';
import { isPositiveNumber, isValueBetweenNumberRange } from '@ubeya/shared/utils/form';

const Container = styled(FlexColumn)`
  gap: 16px;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.grey4};
  margin-bottom: 4px;
`;

const OptionalText = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
  margin-left: 4px;
`;

const RowContainer = styled(FlexMiddle)`
  gap: 16px;
  flex-wrap: wrap;
  > div {
    flex: 1;
    min-width: 250px;
  }
`;

const EmployeeContractCreate = ({ accountContracts, accountRequiresContract }) => {
  const { t, currency } = useContext(ConfigContext);
  const form = useForm();
  const previousContractIdRef = useRef(null);

  const {
    input: { value: selectedContractId }
  } = useField('contractId');

  const contractOptions = useMemo(
    () =>
      (accountContracts || [])
        .filter(({ isActive }) => isActive)
        .map((contract) => ({
          label: contract.name,
          value: contract.id
        })),
    [accountContracts]
  );

  const selectedContract = useMemo(() => {
    if (!selectedContractId) return null;
    return accountContracts?.find((contract) => contract.id === selectedContractId);
  }, [selectedContractId, accountContracts]);

  const shouldViewMinimumOptions = selectedContract && ContractTypesWithMinimums.includes(selectedContract.type);
  const isCasualContract = selectedContract?.type === ACCOUNTS_CONTRACTS_TYPES.Casual;

  // Pre-fill contract default values when contract changes
  useEffect(() => {
    const hasContractChanged = previousContractIdRef.current !== selectedContractId;

    if (selectedContract && !isCasualContract && hasContractChanged) {
      const prefillConfig = [
        { formField: 'contractMinimumWeeklyWorkingHours', contractField: 'minimumWeeklyWorkingHours' },
        { formField: 'contractMinimumHourlyRateOfPay', contractField: 'minimumHourlyRateOfPay' },
        { formField: 'contractHolidayAccrualYearlyEntitlement', contractField: 'holidayAccrualYearlyEntitlement' },
        { formField: 'contractMaxDaysConsumeInWeek', contractField: 'maxDaysConsumeInWeek' },
        { formField: 'contractSickDaysYearlyEntitlement', contractField: 'sickDaysYearlyEntitlement' }
      ];

      // Update all fields when contract changes
      prefillConfig.forEach(({ formField, contractField }) => {
        const contractValue = selectedContract[contractField];
        if (contractValue !== null && contractValue !== undefined) {
          form.change(formField, contractValue);
        }
      });

      previousContractIdRef.current = selectedContractId;
    }
  }, [selectedContract, selectedContractId, isCasualContract, form]);

  return (
    <Container>
      <Label>
        {t('contractTypes_assignedContract')}
        {!accountRequiresContract && <OptionalText>({t('optional')})</OptionalText>}
      </Label>
      <SelectField
        name="contractId"
        placeholder={t('pleaseSelect')}
        usePortal
        isMulti={false}
        clearable={true}
        options={contractOptions}
        required={accountRequiresContract}
      />

      {selectedContractId && (
        <>
          <DateField
            name="contractStartDate"
            title={t('contractTypes_employeeContractStartingDate')}
            clearable={false}
            usePortal
            required={accountRequiresContract}
          />

          {!isCasualContract && (
            <>
              <AccordionSummaryContainer>
                <Header2> {t('contractTypes_employeeSpecificContractTerms')} </Header2>
              </AccordionSummaryContainer>

              {shouldViewMinimumOptions && (
                <RowContainer>
                  <InputField
                    title={t('contractTypes_minimumWeeklyWorkingHours')}
                    name="contractMinimumWeeklyWorkingHours"
                    type="number"
                    validate={isPositiveNumber}
                  />
                  <InputField
                    title={`${t('contractTypes_minimumHourlyRateOfPay')} (${currency})`}
                    name="contractMinimumHourlyRateOfPay"
                    type="number"
                    validate={isPositiveNumber}
                  />
                </RowContainer>
              )}

              <RowContainer>
                <InputField
                  name="contractHolidayAccrualYearlyEntitlement"
                  title={t('contractTypes_holidayAccrualYearlyEntitlement')}
                  type="number"
                  validate={isPositiveNumber}
                />
                <InputField
                  name="contractMaxDaysConsumeInWeek"
                  title={t('contractTypes_maxDaysToConsumeInAWeek')}
                  type="number"
                  validate={(value) => isValueBetweenNumberRange({ value, min: 1, max: 7, isRequired: false })}
                />
              </RowContainer>

              <RowContainer>
                <InputField
                  name="contractSickDaysYearlyEntitlement"
                  title={t('contractTypes_sickDaysYearlyEntitlement')}
                  type="number"
                  validate={isPositiveNumber}
                />
              </RowContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default EmployeeContractCreate;
